import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { capitalize, titleCase } from "portal/utils/strings";
import { clearHistory, updateHistory } from "portal/state/location";
import {
  ConfiguredJob,
  configureJob,
  rtcJobsApi,
} from "common/state/rtcJobsApi";
import { current, Draft } from "@reduxjs/toolkit";
import { DateTime } from "luxon";
import { DetailsPanel } from "./DetailsPanel";
import { farmApi } from "common/state/portalApi/farm";
import { FullViewportPage } from "portal/components/Page";
import { Header } from "portal/components/header/Header";
import {
  JobConfigUpdate,
  JobUpdate,
  ObjectiveUpdate,
  UpdateType,
} from "protos/rtc/web_socket";
import { jobExplorer, JobSelectionType } from "common/state/jobExplorer";
import { JobNav } from "../JobNav";
import { JobType } from "protos/atk/job_type";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import {
  Objective,
  ObjectiveAssignment,
  PivotRiskObjectivesResponse,
  State,
} from "protos/rtc/jobs";
import { OrchestrationMap } from "./OrchestrationMap";
import { Path } from "portal/utils/routing";
import { rtcLocatorApi } from "common/state/rtcLocatorApi";
import { SelectionPanel } from "./SelectionPanel";
import { skipToken } from "@reduxjs/toolkit/query";
import { useAppDispatch } from "portal/state/hooks";
import {
  useDeviceLocations,
  useTractorLocations,
} from "common/hooks/useLiveRtcLocation";
import { useDispatch, useSelector } from "react-redux";
import {
  useJobConfigSubscription,
  useJobSubscription,
} from "common/hooks/useLiveJobData";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import SettingsIcon from "@mui/icons-material/Settings";
import TaskIcon from "@mui/icons-material/FormatListBulleted";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import TractorIcon from "@mui/icons-material/Agriculture";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const PIVOT_FORECAST_AUTO_REFRESH_INTERVAL_S = 60;
const DEFAULT_FORECAST_M = "5";

export const OrchestratePage: FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { jobId } = useParams();
  const dispatch = useAppDispatch();
  const { data: configuredJob } =
    rtcJobsApi.endpoints.getConfiguredJob.useQuery(
      { id: jobId ?? "" },
      { skip: !jobId }
    );
  // job and config messages are whole records, so they update the cache
  // instead of refetching; a recipe may return a replacement
  const updateConfiguredJob = useCallback(
    (
      recipe: (draft: Draft<ConfiguredJob>) => ConfiguredJob | undefined | void
    ) => {
      if (jobId) {
        dispatch(
          rtcJobsApi.util.updateQueryData(
            "getConfiguredJob",
            { id: jobId },
            recipe
          )
        );
      }
    },
    [dispatch, jobId]
  );
  const jobCallback = useCallback(
    (j: JobUpdate) => {
      if (!j.job) {
        return;
      }
      // progress and state writes publish the job row without objectives or
      // the robot whitelist
      const { objectives, robotWhitelist, ...fields } = j.job;
      updateConfiguredJob((draft) => {
        Object.assign(draft.job, fields);
        if (objectives.length > 0) {
          draft.job.objectives = objectives;
        }
        if (robotWhitelist) {
          draft.job.robotWhitelist = robotWhitelist;
        }
      });
    },
    [updateConfiguredJob]
  );
  const jobConfigCallback = useCallback(
    (u: JobConfigUpdate) => {
      const config = u.configuration;
      if (!config) {
        return;
      }
      updateConfiguredJob((draft) => configureJob(current(draft).job, config));
    },
    [updateConfiguredJob]
  );
  useJobConfigSubscription({
    jobId: jobId ?? "",
    onJobConfigUpdate: jobConfigCallback,
    skip: !jobId,
  });
  const [objectives, setObjectives] = useState<Objective[]>([]);
  useEffect(() => {
    setObjectives(configuredJob?.job.objectives ?? []);
  }, [configuredJob?.job.objectives]);
  const onObjectiveUpdate = useCallback((o: ObjectiveUpdate) => {
    switch (o.type) {
      case UpdateType.UPDATE_TYPE_CREATE: {
        setObjectives((os) => {
          // if it's already there, treat it as an update
          if (os.some((ob) => ob.id === o.objective?.id)) {
            return os.map((ob) =>
              ob.id === o.objective?.id ? o.objective : ob
            );
          }
          if (o.objective) {
            return [...os, o.objective];
          }
          return os;
        });
        break;
      }
      case UpdateType.UPDATE_TYPE_UPDATE: {
        setObjectives((os) =>
          os.map((ob) => (ob.id === o.objective?.id ? o.objective : ob))
        );
        break;
      }
      case UpdateType.UPDATE_TYPE_DELETE: {
        setObjectives((os) => os.filter((ob) => ob.id !== o.objective?.id));
        break;
      }
      default: {
        break;
      }
    }
  }, []);
  useJobSubscription({
    jobId: jobId?.toString() ?? "",
    onJobUpdate: jobCallback,
    onObjectiveUpdate,
    skip: !jobId,
  });

  const [showTractorHistory, setShowTractorHistory] = useState<boolean>(false);
  const [showPivotForecast, setShowPivotForecast] = useState<boolean>(false);
  const [forecastMinutesInput, setForecastMinutesInput] =
    useState<string>(DEFAULT_FORECAST_M);
  const [forecastMinutes, setForecastMinutes] =
    useState<string>(DEFAULT_FORECAST_M);
  const [pivotRiskData, setPivotRiskData] =
    useState<PivotRiskObjectivesResponse>();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState<boolean>(false);

  const {
    data: pivotRiskObjectives,
    refetch: refetchPivotRiskObjectives,
    isFetching: isFetchingPivotRiskObjectives,
  } = useQueryPopups(
    rtcJobsApi.endpoints.getPivotRiskObjectives.useQuery(
      jobId && showPivotForecast
        ? { jobId, forecast: `${Number.parseInt(forecastMinutes) || 0}m` }
        : skipToken
    )
  );

  useEffect(() => {
    if (pivotRiskObjectives) {
      setPivotRiskData(pivotRiskObjectives);
    }
  }, [pivotRiskObjectives]);

  useEffect(() => {
    if (isFetchingPivotRiskObjectives) {
      const timer = setTimeout(() => {
        setShowLoadingSpinner(true);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setShowLoadingSpinner(false);
    }
  }, [isFetchingPivotRiskObjectives]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setForecastMinutes(forecastMinutesInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [forecastMinutesInput]);

  useEffect(() => {
    if (!showPivotForecast) {
      return;
    }

    const interval = setInterval(() => {
      refetchPivotRiskObjectives();
    }, PIVOT_FORECAST_AUTO_REFRESH_INTERVAL_S * 1000);

    return () => clearInterval(interval);
  }, [showPivotForecast, forecastMinutes, refetchPivotRiskObjectives]);

  useEffect(() => {
    dispatch(jobExplorer.actions.setJob(configuredJob?.job));
  }, [configuredJob, dispatch]);

  const { data: assignments } = useQueryPopups(
    rtcJobsApi.endpoints.listAssignments.useQuery(
      {
        objectiveIds: configuredJob?.job.objectives.map((o) => `${o.id}`),
      },
      {
        refetchOnMountOrArgChange: 5,
        pollingInterval: 5000,
        skip: !configuredJob?.job.objectives,
      }
    )
  );

  useEffect(() => {
    const assignmentsByTractorId: Record<string, ObjectiveAssignment[]> = {};

    for (const assignment of assignments?.assignments ?? []) {
      const currentList = assignmentsByTractorId[assignment.robotSerial];
      if (currentList) {
        assignmentsByTractorId[assignment.robotSerial]?.push(assignment);
      } else {
        assignmentsByTractorId[assignment.robotSerial] = [assignment];
      }
    }

    dispatch(
      jobExplorer.actions.setAssignmentsByTractorSerial(assignmentsByTractorId)
    );
  }, [assignments, dispatch]);

  const { data: availableTractors } = useQueryPopups(
    rtcLocatorApi.endpoints.listRobots.useQuery(
      configuredJob
        ? {
            serials: configuredJob.job.robotWhitelist?.entries.map(
              (r) => r.robotSerial
            ),
          }
        : skipToken
    )
  );

  const tractorSerials = useMemo(
    () =>
      availableTractors ? availableTractors.robots.map((r) => r.serial) : [],
    [availableTractors]
  );

  useTractorLocations(tractorSerials, (resp) => {
    // if the job hasn't ended yet, store the live tractor histories
    if (!configuredJob?.job.endedAt && showTractorHistory) {
      dispatch(updateHistory({ serial: resp.serial, updates: [resp] }));
    }
  });

  const { data: farm } = useQueryPopups(
    farmApi.endpoints.getFarm.useQuery(
      configuredJob ? configuredJob.job.farmId : skipToken
    )
  );
  useEffect(() => {
    dispatch(jobExplorer.actions.setFarm(farm));
  }, [farm, dispatch]);

  // todo: re-evaluate if we move to specifying co-located zones
  const field = farm?.zones.find(
    (z) => z.id?.id === configuredJob?.job.fieldId
  );
  const centerPivotIds =
    field?.contents?.field?.centerPivotIds.map((i) => i.id) ?? [];

  const endpointIds =
    farm?.zones
      .filter(
        (z) =>
          z.id?.id &&
          z.contents?.centerPivot &&
          centerPivotIds.includes(z.id.id)
      )
      .flatMap(
        (z) => z.contents?.centerPivot?.legs.at(0)?.endpointDeviceId ?? []
      ) ?? [];
  useDeviceLocations(endpointIds);

  const jobLink = configuredJob
    ? `${Path.JOBS}/${configuredJob.job.id}`
    : Path.JOBS;
  const parentLink = searchParams.get("returnTo") ?? jobLink;

  if (configuredJob?.job.state === State.PENDING || jobId === undefined) {
    return <Navigate to={parentLink} />;
  }

  return (
    <>
      <Header
        title={capitalize(t("models.jobs.job_one"))}
        pageTitle={`${
          configuredJob?.job.name ?? t("utils.descriptors.loading")
        } - ${capitalize(t("models.jobs.job_one"))}`}
        parentLink={parentLink}
      >
        <JobNav jobId={jobId} />
      </Header>
      <FullViewportPage>
        <div className="flex flex-col h-full">
          {configuredJob && (
            <OrchestrateView
              configuredJob={configuredJob}
              objectives={objectives}
              showTractorHistory={showTractorHistory}
              setShowTractorHistory={(b: boolean) => setShowTractorHistory(b)}
              showPivotForecast={showPivotForecast}
              setShowPivotForecast={(b: boolean) => setShowPivotForecast(b)}
              pivotRiskData={pivotRiskData}
              forecastMinutesInput={forecastMinutesInput}
              setForecastMinutesInput={setForecastMinutesInput}
              loadingPivotForecast={showLoadingSpinner}
            />
          )}
        </div>
      </FullViewportPage>
    </>
  );
};

interface OrchestrateViewProps {
  configuredJob: ConfiguredJob;
  objectives: Objective[];
  showTractorHistory: boolean;
  setShowTractorHistory: (b: boolean) => void;
  showPivotForecast: boolean;
  setShowPivotForecast: (b: boolean) => void;
  pivotRiskData?: PivotRiskObjectivesResponse;
  forecastMinutesInput: string;
  setForecastMinutesInput: (value: string) => void;
  loadingPivotForecast: boolean;
}
const OrchestrateView: FC<OrchestrateViewProps> = ({
  configuredJob,
  objectives,
  showTractorHistory,
  setShowTractorHistory,
  showPivotForecast,
  setShowPivotForecast,
  pivotRiskData,
  forecastMinutesInput,
  setForecastMinutesInput,
  loadingPivotForecast,
}) => {
  const { t } = useTranslation();
  const showDetails = useSelector(jobExplorer.selectors.selectShowDetails);
  const { setSelection } = jobExplorer.actions;
  const [showAllTasks, setShowAllTasks] = useState<boolean>(
    configuredJob.job.type === JobType.TRANSPORT
  );
  const [showFrontAxlePath, setShowFrontAxlePath] = useState<boolean>(false);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const dispatch = useDispatch();
  // When we get a live update, we want to avoid refetching the whole history
  // using rtk's caching lets us do that easily.
  const [useCachedHistory, setUseCachedHistory] = useState<boolean>(false);
  const rigId = configuredJob.groundPrep?.rigId;
  const { data: rigResp } = atkEquipmentApi.endpoints.getRig.useQuery(
    {
      id: rigId ?? "",
      validate: true,
    },
    { skip: !rigId }
  );
  const rig = rigResp?.validationResult?.effectiveConfig;

  const job = configuredJob.job;
  const [fetchLocation] =
    rtcLocatorApi.endpoints.getTractorLocationHistory.useLazyQuery();

  useEffect(() => {
    let cancelled = false;

    const getHistory = async (): Promise<void> => {
      if (!job.robotWhitelist || cancelled) {
        return;
      }

      setLoadingHistory(true);

      for (const robot of job.robotWhitelist.entries) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (cancelled) {
          break;
        }

        // If the job has started, use the job's startedAt time for history retrieval, otherwise
        // fall back to using the updatedAt time (for READY jobs that haven't quite started yet).
        let startTime: string | undefined;
        if (job.startedAt) {
          startTime = `${DateTime.fromISO(job.startedAt).toUnixInteger()}`;
        } else if (job.updatedAt) {
          startTime = `${DateTime.fromISO(job.updatedAt).toUnixInteger()}`;
        }

        let nextPageToken;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        while (nextPageToken !== null && !cancelled) {
          const { data } = await fetchLocation(
            {
              robotSerial: robot.robotSerial,
              start: startTime,
              end: job.endedAt
                ? `${DateTime.fromISO(job.endedAt).toUnixInteger()}`
                : undefined,
              includeClosest: true,
              desc: false,
              pageSize: 5000,
              pageToken: nextPageToken,
            },
            useCachedHistory
          );

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (cancelled) {
            break;
          }

          nextPageToken =
            !data?.nextPageToken || data.nextPageToken === nextPageToken
              ? // eslint-disable-next-line unicorn/no-null
                null
              : data.nextPageToken;

          if (data?.history?.records) {
            dispatch(
              updateHistory({
                serial: robot.robotSerial,
                updates: data.history.records,
              })
            );
          }
        }
      }
    };
    if (showTractorHistory) {
      getHistory();
    }
    return () => {
      cancelled = true;
      setLoadingHistory(false);
    };
  }, [job, fetchLocation, showTractorHistory, dispatch, useCachedHistory]);

  if (!showTractorHistory) {
    dispatch(clearHistory());
    if (useCachedHistory) {
      setUseCachedHistory(false);
    }
  }

  return (
    <FullViewportPage>
      <div className="flex flex-col w-full h-full pt-20 pb-10">
        <div className="flex gap-4">
          <Typography variant="h3">{configuredJob.job.name}</Typography>
          <IconButton
            className="flex my-auto"
            color="info"
            onClick={() =>
              dispatch(
                setSelection({ type: JobSelectionType.JOB, selection: job })
              )
            }
          >
            <SettingsIcon />
          </IconButton>
          <Button
            variant="contained"
            onClick={() => setShowAllTasks(!showAllTasks)}
            className="flex my-auto"
            startIcon={
              showAllTasks ? <VisibilityOffIcon /> : <VisibilityIcon />
            }
          >
            <TaskIcon />
          </Button>
          <Paper className="flex my-auto pl-4 rounded-md">
            <FormControl>
              <FormControlLabel
                control={
                  <Switch
                    value={showTractorHistory}
                    onClick={() => setShowTractorHistory(!showTractorHistory)}
                    disabled={job.state === State.PENDING}
                  />
                }
                label={
                  <div className="flex gap-2">
                    <TractorIcon className="flex my-auto" />
                    {titleCase(t("views.autotractor.history"))}
                    {loadingHistory && (
                      <CircularProgress
                        className="flex my-auto"
                        size={12}
                        color="info"
                      />
                    )}
                  </div>
                }
              />
            </FormControl>
          </Paper>
          <Paper className="flex my-auto pl-4 rounded-md">
            <FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={showFrontAxlePath}
                    onClick={() => setShowFrontAxlePath(!showFrontAxlePath)}
                  />
                }
                label={t("views.autotractor.frontAxlePath")}
              />
            </FormControl>
          </Paper>
          {!configuredJob.transport && (
            <Paper className="flex my-auto pl-4 rounded-md">
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showPivotForecast}
                      onClick={() => setShowPivotForecast(!showPivotForecast)}
                    />
                  }
                  label={
                    <div className="flex items-center gap-2">
                      <TrackChangesIcon className="flex my-auto" />
                      {titleCase(t("views.autotractor.pivotForecast"))}
                      {showPivotForecast && (
                        <TextField
                          type="number"
                          value={forecastMinutesInput}
                          onChange={(e) => {
                            const value = e.target.value;
                            setForecastMinutesInput(value);
                          }}
                          size="small"
                          sx={{ width: 100, minWidth: 100 }}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  {t("utils.units.min")}
                                </InputAdornment>
                              ),
                            },

                            htmlInput: { min: 0, step: 1 },
                          }}
                        />
                      )}
                      {loadingPivotForecast && (
                        <CircularProgress
                          className="flex my-auto"
                          size={12}
                          color="info"
                        />
                      )}
                    </div>
                  }
                />
              </FormControl>
            </Paper>
          )}
        </div>
        <div className="flex w-full h-full overflow-y-hidden relative">
          <div className="flex w-1/5 h-full">
            <SelectionPanel objectives={objectives} />
          </div>
          <div className="flex w-full h-full">
            <OrchestrationMap
              configuredJob={configuredJob}
              objectives={objectives}
              showAllTasks={showAllTasks}
              showTractorHistory={showTractorHistory}
              showPivotForecast={showPivotForecast}
              showFrontAxlePath={showFrontAxlePath}
              pivotRiskData={pivotRiskData}
              rig={rig}
            />
          </div>
          {showDetails && (
            <div className="flex w-full h-full max-w-1/4 absolute right-0">
              <DetailsPanel />
            </div>
          )}
        </div>
      </div>
    </FullViewportPage>
  );
};
